import { failHard, failLoud } from './fail-loud';

/**
 * Widget resolution (#1799).
 *
 * The order is fixed and has exactly three levels:
 *
 *   1. `item.widget`  — what this view asked for;
 *   2. `field.widget` — the model's default for the field;
 *   3. the type default — the closed per-`FieldType` widget set.
 *
 * A named widget that is not registered, or one pointed at a type it declares
 * no support for, is an authoring mistake: it throws in dev and test, and in
 * prod logs and degrades to the type default (level 3). A type with no default
 * widget has nothing to degrade to and throws everywhere.
 *
 * Resolution answers with a COMPLETE widget: of the five projections a widget
 * declares, four are optional (`csvText` / `filterType` / `zodBase` / `input`),
 * and an omitted one is filled from the type default of the field type being
 * rendered (#1802, `input` added by ADR-0030). That filling happens here rather
 * than at each consumer so a CSV export and a filter row cannot disagree about
 * what "no csvText" means, and so a read-only widget does not have to hand-copy
 * its own field type's control just to satisfy the contract (#1889).
 *
 * The catalog is generic over the widget representation so core owns the
 * ORDER and the FAILURE POLICY without owning the host's rendering types — the
 * host binds its own widget shape when it builds the catalog.
 */

/**
 * The projections a widget may omit. Core knows their NAMES (it owns the
 * fallback order) but not their types — those are the host's.
 */
const FALLBACK_PROJECTIONS = ['csvText', 'filterType', 'zodBase', 'input'] as const;

export type FallbackProjection = (typeof FALLBACK_PROJECTIONS)[number];

/** The only thing core requires of a widget: it may carry those projections. */
export type ProjectingWidget = { readonly [K in FallbackProjection]?: unknown };

export interface NamedWidgetEntry<W> {
  widget: W;
  /** Field types this widget can render. Empty ⇒ it renders nothing. */
  supportedTypes: readonly string[];
}

export interface WidgetCatalog<W> {
  named: ReadonlyMap<string, NamedWidgetEntry<W>>;
  typeDefaults: ReadonlyMap<string, W>;
}

export interface WidgetCatalogSource<W> {
  named?: Readonly<Record<string, NamedWidgetEntry<W>>>;
  typeDefaults: Readonly<Record<string, W>>;
}

export function createWidgetCatalog<W extends ProjectingWidget>(
  source: WidgetCatalogSource<W>,
): WidgetCatalog<W> {
  return {
    named: new Map(Object.entries(source.named ?? {})),
    typeDefaults: new Map(Object.entries(source.typeDefaults)),
  };
}

export interface WidgetRequest {
  /** Level 1: the view item's override. */
  itemWidget?: string;
  /** Level 2: the model field's default. */
  fieldWidget?: string;
  /** Level 3 key: the field type whose default closes the chain. */
  type: string;
}

function typeDefaultOf<W extends ProjectingWidget>(catalog: WidgetCatalog<W>, type: string): W {
  const fallback = catalog.typeDefaults.get(type);
  if (fallback === undefined) {
    return failHard(`no default widget is registered for field type "${type}"`);
  }
  return fallback;
}

/**
 * Level 3 of the resolution order applied per projection instead of per
 * widget. A widget that supplies a projection always keeps it; one that omits
 * it borrows the type default's. A type with no default has nothing to lend,
 * which is not an error here — a field of that type with no widget at all is
 * already caught by `typeDefaultOf`.
 */
function withTypeDefaultProjections<W extends ProjectingWidget>(
  widget: W,
  typeDefault: W | undefined,
): W {
  if (typeDefault === undefined || typeDefault === widget) return widget;

  const patch: { -readonly [K in FallbackProjection]?: unknown } = {};
  let patched = false;
  for (const key of FALLBACK_PROJECTIONS) {
    if (widget[key] !== undefined || typeDefault[key] === undefined) continue;
    patch[key] = typeDefault[key];
    patched = true;
  }
  if (!patched) return widget;

  // The patch only ever refills keys `W` already declares, so the spread is
  // still a `W`; TS cannot see that through an unresolved type parameter.
  return { ...widget, ...patch } as W;
}

function selectWidget<W extends ProjectingWidget>(
  catalog: WidgetCatalog<W>,
  request: WidgetRequest,
): W {
  const name = request.itemWidget ?? request.fieldWidget;
  if (name === undefined) return typeDefaultOf(catalog, request.type);

  const entry = catalog.named.get(name);
  if (entry === undefined) {
    failLoud(`widget "${name}" is not registered`);
    return typeDefaultOf(catalog, request.type);
  }
  if (!entry.supportedTypes.includes(request.type)) {
    failLoud(`widget "${name}" does not support field type "${request.type}"`);
    return typeDefaultOf(catalog, request.type);
  }
  return entry.widget;
}

export function resolveWidget<W extends ProjectingWidget>(
  catalog: WidgetCatalog<W>,
  request: WidgetRequest,
): W {
  return withTypeDefaultProjections(
    selectWidget(catalog, request),
    catalog.typeDefaults.get(request.type),
  );
}
